import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import type { Row } from '../lib/db-types'
import { useUserScopedCrud } from './useUserScopedCrud'

// User-facing shape: we never need `user_id`/`created_at` in the UI.
// `icon` is an optional emoji glyph (added 2026-04-25 per ADR 0027) —
// defaults to '' for "no icon", so a coloured dot still shows when the
// user hasn't picked one.
// `team_id` (added 2026-04-26 per ADR 0028) is non-null when the tag
// has been shared with a team. The UI uses truthiness-of-team_id to
// decide whether to show a "shared with team" indicator.
// `user_id` is surfaced so the UI can tell whether the current user is
// the creator (only creators — or team owners — can edit/share/unshare
// a tag, enforced by RLS).
export type Tag = Pick<
  Row<'tags'>,
  'id' | 'name' | 'color' | 'icon' | 'sort_order' | 'team_id' | 'user_id'
>
export type TagAssignment = Pick<
  Row<'tag_assignments'>,
  'id' | 'tag_id' | 'conversation_urn'
>

// --- Tag definitions CRUD -----------------------------------------------------

/**
 * Tags are user-scoped by design — each user's own organisational tags,
 * never shared across a team. Only snippets (templates) have a team
 * dimension.
 */
export const tagsKey = (userId: string | undefined): QueryKey => ['tags', userId ?? 'anon']

type TagUpdate = Partial<Pick<Tag, 'name' | 'color' | 'icon' | 'sort_order' | 'team_id'>>
type TagAddInput = { name: string; color: string; icon?: string; teamId?: string | null }

export function useTags() {
  // Thin adapter over useUserScopedCrud. Temp-id optimistic add restored
  // via `optimisticRow`: the new tag appears in the sidebar on the same
  // frame the user clicks "Add", and the temp id is swapped for the real
  // one when the server responds. On failure the temp row is filtered out
  // and the mutation rejects so callers can surface a toast.
  const { user } = useAuth()
  const crud = useUserScopedCrud<Tag, TagAddInput, TagUpdate>({
    table: 'tags',
    select: 'id, name, color, icon, sort_order, team_id, user_id',
    queryKey: tagsKey,
    rowForInsert: ({ name, color, icon, teamId }, userId, nextSortOrder) => ({
      user_id: userId,
      name,
      color,
      icon: icon ?? '',
      team_id: teamId ?? null,
      sort_order: nextSortOrder,
    }),
    rowForUpdate: (updates) => ({ ...updates }),
    optimisticRow: ({ name, color, icon, teamId }, tempId, nextSortOrder) => ({
      id: tempId,
      name,
      color,
      icon: icon ?? '',
      team_id: teamId ?? null,
      // user_id is filled from the caller since every insert happens under
      // the authenticated user. useUserScopedCrud passes user.id into
      // rowForInsert; the optimistic row mirrors that for UI-level
      // "is this mine?" checks before the server round-trip returns.
      user_id: user?.id ?? '',
      sort_order: nextSortOrder,
    }),
  })

  return {
    tags: crud.items,
    loading: crud.loading,
    // Callers `await` these and can .catch()/try-catch for toast surfacing.
    // Previously these wrappers swallowed rejections with a `/* surfaced */`
    // comment referring to a version of the hook that returned the mutation
    // object — it hasn't for a while, so errors were silently eaten with no
    // way for UI to know. Returning the raw mutateAsync promise lets callers
    // handle errors, and unhandled rejections still show up in the console
    // for debugging.
    addTag: (name: string, color: string, icon: string = '', teamId: string | null = null) =>
      crud.add({ name, color, icon, teamId }),
    updateTag: (tagId: string, updates: TagUpdate) => crud.update(tagId, updates),
    deleteTag: (tagId: string) => crud.remove(tagId),
    /**
     * Share a personal tag with `teamId`, or pass `null` to unshare. Implemented
     * on top of `updateTag` so the optimistic-snapshot path carries. RLS
     * rejects shares to teams the user isn't in, and unshare-by-non-creator
     * (who must also not be a team owner — the check is OR'd). Surfaces the
     * rejection via the mutation's rejected promise.
     */
    setTagTeam: (tagId: string, teamId: string | null) =>
      crud.update(tagId, { team_id: teamId }),
  }
}

// --- Tag assignments (conversation ↔ tag mapping) ----------------------------

export const tagAssignmentsKey = (userId: string | undefined): QueryKey => ['tagAssignments', userId ?? 'anon']

export function useTagAssignments() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = tagAssignmentsKey(user?.id)

  const query = useQuery<TagAssignment[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tag_assignments')
        .select('id, tag_id, conversation_urn')
      if (error) throw new Error(error.message)
      return (data ?? []) as TagAssignment[]
    },
  })

  // Realtime invalidation is handled centrally by <RealtimeBridge />.

  const assignMut = useMutation({
    mutationKey: ['tagAssignments', 'assign'],
    mutationFn: async ({ tagId, conversationUrn }: { tagId: string; conversationUrn: string }): Promise<TagAssignment> => {
      if (!user) throw new Error('Not authenticated')
      const current = qc.getQueryData<TagAssignment[]>(key) ?? []
      // Idempotent: if the assignment already exists, return the
      // existing row instead of undefined. Previously this path
      // resolved the promise with no value, making the return type a
      // lie and confusing any caller that wanted to reference the
      // just-assigned row's id.
      const existing = current.find((a) => a.tag_id === tagId && a.conversation_urn === conversationUrn)
      if (existing) return existing
      const tempId = crypto.randomUUID()
      qc.setQueryData<TagAssignment[]>(key, (prev) => [
        ...(prev ?? []),
        { id: tempId, tag_id: tagId, conversation_urn: conversationUrn },
      ])
      const { data, error } = await supabase
        .from('tag_assignments')
        .insert({ user_id: user.id, tag_id: tagId, conversation_urn: conversationUrn })
        .select()
        .single()
      if (error) {
        qc.setQueryData<TagAssignment[]>(key, (prev) => (prev ?? []).filter((a) => a.id !== tempId))
        throw new Error(error.message)
      }
      const inserted = data as TagAssignment
      qc.setQueryData<TagAssignment[]>(key, (prev) =>
        (prev ?? []).map((a) => (a.id === tempId ? inserted : a)),
      )
      return inserted
    },
  })

  const removeMut = useMutation({
    mutationKey: ['tagAssignments', 'remove'],
    mutationFn: async ({ tagId, conversationUrn }: { tagId: string; conversationUrn: string }) => {
      if (!user) throw new Error('Not authenticated')
      const snapshot = qc.getQueryData<TagAssignment[]>(key)
      qc.setQueryData<TagAssignment[]>(key, (prev) =>
        (prev ?? []).filter((a) => !(a.tag_id === tagId && a.conversation_urn === conversationUrn)),
      )
      const { error } = await supabase
        .from('tag_assignments')
        .delete()
        .eq('user_id', user.id)
        .eq('tag_id', tagId)
        .eq('conversation_urn', conversationUrn)
      if (error) {
        qc.setQueryData<TagAssignment[]>(key, snapshot)
        throw new Error(error.message)
      }
    },
  })

  const assignments = query.data ?? []

  // Stable-identity accessors via `useCallback` keyed on `assignments`.
  // Previously these were inline arrow functions — new reference on every
  // render — which caused any `useMemo`/`useCallback` using them as deps
  // (notably Shell.tsx's main `conversations` and `compositeCounts`
  // memos) to thrash on every render. The functional behaviour is
  // identical; only the identity stability is new. Deps array is just
  // `assignments` because both callers that closed over it return new
  // work derived purely from that value.
  const getTagsForConversation = useCallback((conversationUrn: string) =>
    assignments.filter((a) => a.conversation_urn === conversationUrn).map((a) => a.tag_id),
  [assignments])

  const getConversationsForTag = useCallback((tagId: string) =>
    new Set(assignments.filter((a) => a.tag_id === tagId).map((a) => a.conversation_urn)),
  [assignments])

  const getUntaggedConversationUrns = useCallback((allConversationUrns: string[]) => {
    const tagged = new Set(assignments.map((a) => a.conversation_urn))
    return new Set(allConversationUrns.filter((urn) => !tagged.has(urn)))
  }, [assignments])

  const assignTag = useCallback((tagId: string, conversationUrn: string) =>
    assignMut.mutateAsync({ tagId, conversationUrn }),
  [assignMut])

  const removeTagAssignment = useCallback((tagId: string, conversationUrn: string) =>
    removeMut.mutateAsync({ tagId, conversationUrn }),
  [removeMut])

  return {
    assignments,
    loading: !!user && query.isPending,
    assignTag,
    removeTagAssignment,
    getTagsForConversation,
    getConversationsForTag,
    getUntaggedConversationUrns,
  }
}
